// frontend/src/app/ingress/FeedbackWidget.tsx
import React, { useMemo, useState } from "react";
import { ApiError } from "../../lib/api";
import { submitIngressFeedback } from "../../lib/feedbackClient";

const TAGS = [
  "incorrect",
  "unsafe",
  "slow",
  "irrelevant",
  "formatting",
  "hallucination",
  "refused incorrectly",
  "other",
];

export default function FeedbackWidget(props: { ingressRequestId: string; ingressKey?: string }) {
  const { ingressRequestId, ingressKey } = props;

  const [rating, setRating] = useState<"up" | "down" | null>(null);

  // Advanced fields (only shown when expanded)
  const [expanded, setExpanded] = useState(false);
  const [tags, setTags] = useState<string[]>([]);
  const [comment, setComment] = useState("");
  const [corrected, setCorrected] = useState("");
  const [expected, setExpected] = useState("");
  const [sensitive, setSensitive] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [okMsg, setOkMsg] = useState<string | null>(null);
  const [errMsg, setErrMsg] = useState<string | null>(null);

  const canSubmit = useMemo(() => !!ingressRequestId && !!rating && !submitting, [ingressRequestId, rating, submitting]);

  const hasAdvancedValue = useMemo(() => {
    if (tags.length > 0) return true;
    if (comment.trim()) return true;
    if (corrected.trim()) return true;
    if (expected.trim()) return true;
    if (sensitive) return true;
    return false;
  }, [tags, comment, corrected, expected, sensitive]);

  function toggleTag(t: string) {
    setTags((prev) => (prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]));
  }

  async function onSubmit() {
    if (!canSubmit) return;

    setSubmitting(true);
    setOkMsg(null);
    setErrMsg(null);

    try {
      await submitIngressFeedback(
        {
          ingress_request_id: ingressRequestId,
          rating: rating!,
          tags: tags.length ? tags : undefined,
          comment: comment.trim() || undefined,
          corrected_output: corrected.trim() || undefined,
          expected_output: expected.trim() || undefined,
          report_sensitive_output: sensitive,
        },
        ingressKey
      );

      setOkMsg("Thanks — feedback recorded.");
      // keep entries, but collapse to save space
      setExpanded(false);
    } catch (e: any) {
      setErrMsg(e instanceof ApiError ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  }

  function onReset() {
    setRating(null);
    setTags([]);
    setComment("");
    setCorrected("");
    setExpected("");
    setSensitive(false);
    setOkMsg(null);
    setErrMsg(null);
    setExpanded(false);
  }

  return (
    <div className="mt-3">
      {/* Compact bar (always) */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="text-xs text-gray-600 mr-1">Feedback:</div>

        <button
          className={`px-2.5 py-1 rounded border text-sm ${
            rating === "up" ? "bg-green-50 border-green-200" : "hover:bg-gray-50"
          }`}
          type="button"
          onClick={() => {
            setRating("up");
            setOkMsg(null);
            setErrMsg(null);
          }}
          disabled={submitting}
          title="Thumbs up"
        >
          👍
        </button>

        <button
          className={`px-2.5 py-1 rounded border text-sm ${
            rating === "down" ? "bg-red-50 border-red-200" : "hover:bg-gray-50"
          }`}
          type="button"
          onClick={() => {
            setRating("down");
            setOkMsg(null);
            setErrMsg(null);
          }}
          disabled={submitting}
          title="Thumbs down"
        >
          👎
        </button>

        {hasAdvancedValue ? (
          <span className="text-[11px] text-gray-500 border rounded px-2 py-0.5 bg-gray-50">
            details added
          </span>
        ) : null}

        <button
          className="px-3 py-1.5 rounded bg-gray-900 text-white hover:bg-black disabled:opacity-60 text-sm"
          type="button"
          onClick={onSubmit}
          disabled={!canSubmit}
          title={!rating ? "Select thumbs up/down first" : ""}
        >
          {submitting ? "Submitting…" : "Submit"}
        </button>

        <button
          className="px-3 py-1.5 rounded border hover:bg-gray-50 disabled:opacity-60 text-sm"
          type="button"
          onClick={onReset}
          disabled={submitting}
        >
          Reset
        </button>

        <button
          className="px-3 py-1.5 rounded border hover:bg-gray-50 disabled:opacity-60 text-sm flex items-center gap-1"
          type="button"
          onClick={() => setExpanded((v) => !v)}
          disabled={submitting}
          aria-expanded={expanded}
          title={expanded ? "Hide details" : "Add details"}
        >
          <span>{expanded ? "Hide details" : "Add details"}</span>
          <span className="text-xs">{expanded ? "▾" : "▸"}</span>
        </button>
      </div>

      {/* Small status line (compact) */}
      {errMsg ? <div className="mt-2 text-xs text-red-700 whitespace-pre-wrap">{errMsg}</div> : null}
      {okMsg ? <div className="mt-2 text-xs text-green-700">{okMsg}</div> : null}

      {/* Advanced panel (ONLY when expanded) */}
      {expanded ? (
        <div className="mt-3 border rounded p-3 bg-white">
          <div className="text-xs text-gray-600 mb-2">Tags (optional)</div>
          <div className="flex flex-wrap gap-2">
            {TAGS.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => toggleTag(t)}
                className={`text-xs px-2 py-1 rounded border ${
                  tags.includes(t) ? "bg-gray-900 text-white border-gray-900" : "hover:bg-gray-50"
                }`}
                disabled={submitting}
              >
                {t}
              </button>
            ))}
          </div>

          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <div className="text-xs text-gray-600">Comment (optional)</div>
              <textarea
                className="mt-1 w-full border rounded p-2 text-sm h-20"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Tell us what went wrong…"
                disabled={submitting}
              />
            </div>
            <div>
              <div className="text-xs text-gray-600">Corrected output (optional)</div>
              <textarea
                className="mt-1 w-full border rounded p-2 text-sm h-20"
                value={corrected}
                onChange={(e) => setCorrected(e.target.value)}
                placeholder="Paste the corrected output…"
                disabled={submitting}
              />
            </div>
          </div>

          <div className="mt-3">
            <div className="text-xs text-gray-600">Expected answer / ground truth (optional)</div>
            <textarea
              className="mt-1 w-full border rounded p-2 text-sm h-20"
              value={expected}
              onChange={(e) => setExpected(e.target.value)}
              placeholder="Paste the expected answer…"
              disabled={submitting}
            />
          </div>

          <div className="mt-3 flex items-center gap-2">
            <input
              type="checkbox"
              checked={sensitive}
              onChange={(e) => setSensitive(e.target.checked)}
              disabled={submitting}
            />
            <div className="text-xs text-gray-700">Report sensitive output (creates incident flag)</div>
          </div>
        </div>
      ) : null}
    </div>
  );
}